import { type VenueBasedTicketBasketPurchaseRequest } from "@app-contexts/dashboard/features/box-office/data/types/basket";
import { type UserBasedTicketBasketPurchaseRequest } from "@app-contexts/user/features/basket/data/types/basket";
import { DataProvider } from "@shared/constants/common";
import { normalizeAddress } from "@shared/modules/basket/utils/address-utils";

import { type PaymentServiceBasket } from "../../services/useBasketPaymentService";
import { type PurchaseOptsType } from "../../types";

export type PurchaseRequest =
  | VenueBasedTicketBasketPurchaseRequest
  | UserBasedTicketBasketPurchaseRequest;

/**
 * Builds the purchase request payload from basket state and purchase options.
 *
 * Only includes fields the backend purchase serializer actually expects.
 * Previously, gateways spread `...purchaseOpts` into the request which leaked
 * frontend-only fields (name_on_card, wallet_one_time_token, etc.) and
 * bypassed type safety via `as` casts.
 */
export function buildBasePurchaseData(
  basketType: DataProvider,
  basket: PaymentServiceBasket,
  purchaseOpts: PurchaseOptsType = {}
): PurchaseRequest {
  if (basketType === DataProvider.Venue) {
    const data: VenueBasedTicketBasketPurchaseRequest = {
      payment_type: basket.paymentType,
      purchase_source_platform: basket.purchaseSourcePlatform,
    };

    if (purchaseOpts.is_group_sale) {
      data.is_group_sale = true;
    }
    if (purchaseOpts.customer_group) {
      data.customer_group = purchaseOpts.customer_group;
    }
    if (purchaseOpts.exchange_invoice_transaction_id) {
      data.exchange_invoice_transaction_id =
        purchaseOpts.exchange_invoice_transaction_id;
    }

    return data;
  }

  const data: UserBasedTicketBasketPurchaseRequest = {
    payment_type: basket.paymentType,
    purchase_source_platform: basket.purchaseSourcePlatform,
    uuid: basket.basketUuid,
  };

  return data;
}

/**
 * Resolves billing/shipping addresses from purchaseOpts and basket, applying
 * `normalizeAddress` to guard against non-object values.
 */
export function resolveAddresses(
  basket: PaymentServiceBasket,
  purchaseOpts: PurchaseOptsType = {}
): {
  billingAddress: PurchaseRequest["billing_address"];
  shippingAddress: PurchaseRequest["shipping_address"];
} {
  const billingAddress =
    normalizeAddress(purchaseOpts.billing_address) ??
    normalizeAddress(basket.billingAddress) ??
    null;

  const shippingAddress =
    normalizeAddress(purchaseOpts.shipping_address) ??
    normalizeAddress(basket.shippingAddress) ??
    null;

  return { billingAddress, shippingAddress };
}
